import type { VirtualFile } from "@pdfking/pdfengine"
import { CreateRenderFunction, NextFrame } from "@pdfking/reactivity/preact"
import { length, to_array } from "@pdfking/rfunc"
import {
  Items,
  KeyDrop,
  Keys,
  RecordMerge,
  SortBy,
  Values,
} from "@pdfking/tfunc"
import { GENERIC_ERROR } from "@pdfking/translations/enum/errors"
import { BlobUrl } from "@pdfking/workflow/actions/pdfengine/BlobUrl"
import { Icon } from "@pdfking/workflow/components/Icon"
import { IDMixin } from "@pdfking/workflow/mixins/IDMixin"
import { RootMixin } from "@pdfking/workflow/mixins/RootMixin"
import fire_event from "@pdfking/workflow/utils/fire_event"
import make_translations from "@pdfking/workflow/utils/make_translations"
import proxy from "@pdfking/workflow/utils/proxy"
import truncate_chars from "@pdfking/workflow/utils/truncate_chars"
import closest from "../dom/closest"
import download_url from "../files/actions/download_url"
import upload_files from "../files/actions/upload_files"
import new_file_from_datatransfer from "../files/make/new_file_from_datatransfer"
import get_filedata from "../files/naming/get_filedata"
import get_short_name from "../files/naming/get_short_name"
import guess_extension from "../files/naming/guess_extension"
import guess_filecheck from "../files/naming/guess_filecheck"
import { FilenamesMixin } from "../mixins/Filenames"
import storage from "../state/storage"
import user from "../state/user"
import cx from "../utils/cx"
import { TaskProgress } from "./TaskProgress"

function fix_remote_name(path: string, file: VirtualFile) {
  const remote_name = file.file_meta?.remote_name
  if (remote_name) {
    const parsed = new URL(remote_name)
    parsed.pathname = path
    file.file_meta.remote_name = parsed.toString()
  }
  return file
}

export const Uploader = CreateRenderFunction({
  computed: {
    ...proxy(storage(), { selected_files: "global_files" } as const),
    ...proxy(user(), ["is_staff"] as const),
    available_files() {
      return KeyDrop(this.global_files, Keys(this.files))
    },

    sorted_files() {
      return SortBy(Items(this.files), ([path]: [string, any]) =>
        get_short_name(path)
      )
    },
    translations: () => make_translations("actions"),
  },
  props: {
    accept: "" as string,
    class: "",
    clear: false,
    field: "" as string,
    filelist: "" as string,
      multiple: false,
    allow_rename: false,
    on_file_add: null as ((path: string, file: any) => void) | null,
    on_file_change: null as ((path: string, file: any) => void) | null,
    on_file_remove: null as ((path: string, file: any) => void) | null,
    on_file_upload: null as ((files: any) => void) | null,
  },
  data: {
    files: {} as Record<string, any>,
    loading: false,
    upload_task: null,
  },

  methods: {
    add_available_files() {
      for (const [path, file] of Items(this.available_files))
        this.push_file({ path, file })
    },
    async create_file(e: any) {
      if (!this.multiple) this.files = {}

      this.loading = true
      const results = await Promise.all(
        to_array(e.dataTransfer ? e.dataTransfer.files : e.target.files).map(
          new_file_from_datatransfer
        )
      )
      this.loading = false

      for (const result of results) this.push_file(result)

      if (e.target) {
        e.target.value = null
      }
    },
    delete_available_files() {
      storage().unselect_files(Keys(this.available_files))
    },
    delete_file(id?: string) {
      if (id) {
        const f = this.files[id]
        if (f) {
          this.files = KeyDrop(this.files, [id])
          this.on_file_remove?.(id, f)

          if (this.is_staff) storage().add_files({ [id]: f })
        }
      } else {
        for (const k of Keys(this.files)) this.delete_file(k)
      }
    },
    guess_extension,
    guess_filecheck,
    length,
    open_file(path: string, file: any) {
      BlobUrl(file).then((url: string) =>
        download_url(url, get_short_name(path))
      )
    },
    rename_file(path: string, file: any) {
      const { basename, ext } = get_filedata(path)
      const new_basename = prompt("Rename file:", basename)
      if (new_basename === null || !new_basename || new_basename === basename) return

      const new_path = get_short_name(ext ? `${new_basename}.${ext}` : new_basename)
      this.files = RecordMerge(KeyDrop(this.files, [path]), { [new_path]: fix_remote_name(new_path, file) })
    },
    push_file({ path, file }: { path: string; file: VirtualFile }) {
      fix_remote_name(get_short_name(path), file)

      if (this.files[path]) {
        this.on_file_change?.(path, file)
      } else {
        this.on_file_add?.(path, file)
      }
      this.files = RecordMerge(this.files, { [path]: file })
    },
    truncate_chars,
    upload_files(files: Record<string, any>) {
      this.loading = true
      return upload_files(files).then(() => {
        this.on_file_upload?.(files)
        this.loading = false
      })
    },
  },
  mixins: [FilenamesMixin, IDMixin, RootMixin],
  mounted() {
    this.files = { ...this.filenames }

    let submitted = 0

    NextFrame(() => {
      if (this.field) {
        const form = closest(this.root, "form")
        if (form) {
          form.addEventListener("submit", (event: Event) => {
            const remote_keys = Keys(this.files).filter(
              k => this.files[k].file_meta.remote_name
            )
            const local_files = KeyDrop(this.files, remote_keys)

            if (length(Values(local_files)) > 0) {
              if (submitted >= 2) {
                throw GENERIC_ERROR
              }
              event.preventDefault()
              event.stopPropagation()
              this.upload_files(local_files).then(() =>
                fire_event(form, "submit")
              )
              submitted++
            }
          })
        }
      }
    })
  },
  render() {
    return (
      <label
        class={cx("btn btn-outline-light text-start w-100 pe-2", {
          "bg-white": length(this.files) + length(this.available_files),
          "ps-2": length(this.files),
        })}
        for={this.component_id}
        onDrop={(e: DragEvent) => {
          e.preventDefault()
          e.stopPropagation()
          this.create_file(e)
        }}
      >
        <div class="btn-toolbar">
          {this.sorted_files.map(([path, file]: [string, any]) => {
            const short_name = get_short_name(path)
            return (
              <div key={path} class="input-group input-group-sm me-2">
                <a
                  class={cx(
                    "btn p-0 px-1",
                    guess_filecheck(short_name)
                      ? "btn-success text-white"
                      : "btn-light"
                  )}
                  download={short_name}
                  target="_blank"
                  title={short_name}
                  onClick={(e: Event) => {
                    e.preventDefault()
                    e.stopPropagation()
                    this.allow_rename
                      ? this.rename_file(path, file)
                      : this.open_file(path, file)
                  }}
                >
                  {truncate_chars(short_name)}
                </a>
                {file?.file_meta?.remote_name && (
                  <input
                    name={this.field}
                    type="hidden"
                    value={file.file_meta.remote_name}
                  />
                )}
                <button
                  type="button"
                  class={cx(
                    "btn p-0 px-1",
                    guess_filecheck(short_name)
                      ? "btn-success text-white"
                      : "btn-light"
                  )}
                  disabled={this.upload_task}
                  title={this.translations.actions.DELETE}
                  onClick={(e: Event) => {
                    e.preventDefault()
                    e.stopPropagation()
                    this.delete_file(path)
                  }}
                >
                  ×
                </button>
              </div>
            )
          })}

          {length(this.files) == 0 && (
            <div>
              <Icon class="me-1" name="FILE" />

              {this.multiple ? (
                <span>{this.translations.actions.DROP_FILES}</span>
              ) : (
                <span>{this.translations.actions.DROP_FILE}</span>
              )}
              <span class="small fst-italic ms-1 text-muted">
                {this.translations.actions.DROP_OR_CLICK}
              </span>
            </div>
          )}

          {this.loading && <TaskProgress class="ms-auto" />}

          {!this.loading && length(this.available_files) > 0 && (
            <div class="input-group input-group-sm ms-auto">
              <div
                class="btn btn-sm btn-outline-light p-0 px-1"
                onClick={(e: Event) => {
                  e.preventDefault()
                  e.stopPropagation()
                  this.add_available_files()
                }}
              >
                {this.translations.actions.ADD}{" "}
                {`${length(this.available_files)} file`}
              </div>
              <div
                class="btn btn-sm btn-outline-light p-0 px-1"
                title={this.translations.actions.DELETE}
                onClick={(e: Event) => {
                  e.preventDefault()
                  e.stopPropagation()
                  this.delete_available_files()
                }}
              >
                ×
              </div>
            </div>
          )}

          {!this.loading &&
            length(this.available_files) == 0 &&
            length(this.files) > 0 && (
              <div
                class="btn btn-outline-light p-0 px-1 border-0 ms-auto"
                title={this.translations.actions.DELETE}
                onClick={(e: Event) => {
                  e.preventDefault()
                  e.stopPropagation()
                  this.delete_file()
                }}
              >
                ×
              </div>
            )}
        </div>

        <input
          class="d-none"
          id={this.component_id}
          accept={this.accept}
          multiple={!!this.multiple}
          type="file"
          onChange={(e: Event) => {
            e.preventDefault()
            this.create_file(e)
          }}
        />
      </label>
    )
  },
})
